import {put, call, all} from 'redux-saga/effects';
import TaskSeries from "../../entities/taskSeries";
import * as Actions from "../actions/taskSeriesActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type TaskSeriesAction = {
    taskSeriesId: string;
    taskSeries: TaskSeries;
    taskSerieses: TaskSeries[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneTaskSeries(action: TaskSeriesAction) {
	const session = tabSession.captureGeneration();
	try {
		const taskSeries: TaskSeries = yield call(api.findOne, 'taskSeries', action.taskSeriesId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.TASKSERIES_FETCH_ONE_SUCCESS, taskSeriesId: action.taskSeriesId, taskSeries, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.TASKSERIES_FETCH_ONE_FAILURE, taskSeriesId: action.taskSeriesId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {TaskSeriesAction} action
 */
export function* saveTaskSeries(action: TaskSeriesAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ TASKSERIES_SAVE saga', action.taskSeries?.id);

		if (action.taskSeries === undefined || action.taskSeries === null) {
			console.error('saveTaskSeries received a null');
			throw new Error('saveTaskSeries received a null');
		}

		if (!(action.taskSeries instanceof TaskSeries)) {
			action.taskSeries = new TaskSeries(action.taskSeries);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.taskSeries.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.taskSeries);
			const addResult = yield call(idb.add, 'taskSeries', action.taskSeries);
			if (addResult.isErr()) {
				console.error(`⮂ saveTaskSeries: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertTaskSeriesSave(action.taskSeries, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.TASKSERIES_SAVE_FAILURE, taskSeries: action.taskSeries, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.TASKSERIES_SAVE, taskSeries: action.taskSeries, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertTaskSeriesSave(action.taskSeries, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.TASKSERIES_SAVE_FAILURE, taskSeries: action.taskSeries, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {TaskSeriesAction} action
 * @param {TaskSeries[]} action.taskSerieses
 */
export function* saveManyTaskSerieses(action: TaskSeriesAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ TASKSERIES_SAVE_MANY saga', action.taskSerieses.map(taskSeries => taskSeries.id).join(','));

        if (!Array.isArray(action.taskSerieses)) {
            console.error('saveManyTaskSerieses expected an array, received', action.taskSerieses);
            throw new Error('saveManyTaskSerieses expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveTaskSeries), under these queue keys
		const dispatchedAddedOns: number[] = action.taskSerieses.map(taskSeries => taskSeries?.addedOn);
		const revertAll = () => action.taskSerieses.map((taskSeries, i) =>
			put(Actions.revertTaskSeriesSave(taskSeries, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: TaskSeries[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.taskSerieses.length; i++) {
				const taskSeries = action.taskSerieses[i];
				taskSeries.persisted = false;
				ensureOperationId(taskSeries);
				toSave.push(taskSeries);
			}

			const addResult = yield call(idb.add, 'taskSeries', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyTaskSerieses: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.TASKSERIES_SAVE_MANY_FAILURE, taskSerieses: action.taskSerieses, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.TASKSERIES_SAVE_MANY, taskSerieses: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.TASKSERIES_SAVE_MANY_FAILURE, taskSerieses: action.taskSerieses, status: 500, err});

			console.error(`Error saving ${action.taskSerieses.length} taskSerieses to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {TaskSeriesAction} action
 */
export function* removeTaskSeries(action: TaskSeriesAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.taskSeries === undefined || action.taskSeries === null || !(action.taskSeries instanceof TaskSeries)) {
			console.error('removeTaskSeries expected type TaskSeries, received', action.taskSeries);
			reportQueued(false);
			throw new Error('removeTaskSeries expected type TaskSeries');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.taskSeries.status = 'D';
		action.taskSeries.addedOn = newHRTimestamp();
		action.taskSeries.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.taskSeries);
		const addResult = yield call(idb.add, 'taskSeries', action.taskSeries);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeTaskSeries: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.TASKSERIES_REMOVE_FAILURE, taskSeries: action.taskSeries, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TASKSERIES_REMOVE, taskSeries: action.taskSeries});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.TASKSERIES_REMOVE_FAILURE, taskSeries: action.taskSeries, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this TaskSeries.
	*
	* @param {TaskSeriesAction} action
	*/
export function* restoreTaskSeries(action: TaskSeriesAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreTaskSeries expected getCurrentUserId() to return a value');
        return;
    }

	if (action.taskSeries === undefined || action.taskSeries === null) {
        console.error('restoreTaskSeries expected a TaskSeries, received null');
        throw new Error('restoreTaskSeries expected a TaskSeries, received null');
    }
    else if (!(action.taskSeries instanceof TaskSeries)) action.taskSeries = new TaskSeries(action.taskSeries); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.taskSeries.status = 'U';
		action.taskSeries.addedOn = newHRTimestamp();
		action.taskSeries.addedBy = userId;
		action.taskSeries.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.taskSeries);
		const addResult = yield call(idb.add, 'taskSeries', action.taskSeries);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreTaskSeries: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TASKSERIES_RESTORE_FAILURE, taskSeries: action.taskSeries, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TASKSERIES_RESTORE, taskSeries: action.taskSeries});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.TASKSERIES_RESTORE_FAILURE, taskSeries: action.taskSeries, status: 500, err});

		console.error('Error restoring from database', err);
	}
}